import { useReadQuery, useSuspenseQuery, type QueryRef } from '@apollo/client/react';
import { useState } from 'react';

import { withSuspense } from 'src/hoc/withSuspense';
import { DepositStatusEnum, AssetContentTypeEnum, SyncStatusEnum, AssetsOrderByEnum } from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import { createRequiredContext } from 'src/context/createRequiredContext';

import { AssetBlockSkeleton } from '../ui';

import type { IAssetsQuery, IAssetsQueryVariables } from 'src/graphql/actions/asset/assets.query';

const [useAssetsContext, AssetsContext] = createRequiredContext<{
  contentType: AssetContentTypeEnum;
  ui: IUi;
  assets: IAssetsQuery['assets']['nodes'];
  filters: IAssetsQueryVariables;
  totalAssetsCount: number;
  totalFilteredPages: number;
  totalFilteredItems: number;
  latestDeposit: string;
  setDepositStatus: (depositStatus?: DepositStatusEnum) => void;
  setSyncStatus: (syncStatus?: SyncStatusEnum) => void;
  setQueryFilter: (queryFilter?: string) => void;
  setInitialFilters: () => void;
  setPage: (arg: number | ((prevPage: number) => number)) => void;
  openRelabelDialog: (assetIds: string[]) => void;
  closeRelabelDialog: () => void;
  openRemoveDialog: (assetId: string) => void;
  closeRemoveDialog: () => void;
}>();

type Props = {
  contentType: AssetContentTypeEnum;
  queryRef: QueryRef<IAssetsQuery, IAssetsQueryVariables>;
  escrowId: string;
};

type IUi = {
  relabelDialog: {
    isOpen: boolean;
    assetIds: string[];
  };
  removeDialog: {
    isOpen: boolean;
    assetId: string | null;
  };
};

const AssetsContextProvider = withSuspense(
  ({ children, contentType, queryRef, escrowId }: React.PropsWithChildren<Props>) => {
    const [ui, setUi] = useState<IUi>({
      relabelDialog: {
        isOpen: false,
        assetIds: [],
      },
      removeDialog: {
        isOpen: false,
        assetId: null,
      },
    });

    const initialFilters = {
      softwareEscrowId: escrowId,
      perPage: 10,
      page: 1,
      contentType,
    };

    const [filters, setFilters] = useState<IAssetsQueryVariables>(initialFilters);

    const { data: dataAllAssets } = useReadQuery(queryRef);
    const { data: dataTakenAssets } = useSuspenseQuery(AssetsDocument, {
      variables: {
        depositStatus: DepositStatusEnum.Taken,
        softwareEscrowId: escrowId,
        perPage: 1,
        page: 1,
        contentType,
        orderBy: AssetsOrderByEnum.LatestDeposit,
      },
      skip: !dataAllAssets?.assets.nodesCount,
    });
    const { data: dataAssets } = useSuspenseQuery(AssetsDocument, {
      variables: filters,
      skip: !dataAllAssets?.assets.nodesCount,
    });

    const setDepositStatus = (depositStatus?: DepositStatusEnum) =>
      setFilters((prevFilters) => ({ ...prevFilters, depositStatus }));

    const setSyncStatus = (syncStatus?: SyncStatusEnum) =>
      setFilters((prevFilters) => ({ ...prevFilters, syncStatus }));

    const setQueryFilter = (queryFilter?: string) =>
      setFilters((prevFilters) => ({ ...prevFilters, queryFilter, page: 1 }));

    const setPage = (arg: number | ((prevPage: number) => number)) =>
      setFilters((prevFilters) => {
        const prevPage = prevFilters.page ?? 1;
        const nextPage = typeof arg === 'function' ? arg(prevPage) : arg;
        return { ...prevFilters, page: nextPage };
      });

    const setInitialFilters = () => setFilters(initialFilters);

    const openRelabelDialog = (assetIds: string[]) =>
      setUi((prevUi) => ({ ...prevUi, relabelDialog: { isOpen: true, assetIds } }));

    const closeRelabelDialog = () => setUi((prevUi) => ({ ...prevUi, relabelDialog: { isOpen: false, assetIds: [] } }));

    const openRemoveDialog = (assetId: string) =>
      setUi((prevUi) => ({ ...prevUi, removeDialog: { isOpen: true, assetId } }));

    const closeRemoveDialog = () => setUi((prevUi) => ({ ...prevUi, removeDialog: { isOpen: false, assetId: null } }));

    const values = {
      contentType,
      ui,
      totalAssetsCount: dataAllAssets?.assets.nodesCount || 0,
      totalFilteredPages: dataAssets?.assets.pagesCount || 0,
      totalFilteredItems: dataAssets?.assets.nodesCount || 0,
      latestDeposit: dataTakenAssets?.assets.nodes[0]?.latestDeposit,
      assets: dataAssets?.assets.nodes || [],
      filters,
      setDepositStatus,
      setSyncStatus,
      setQueryFilter,
      setInitialFilters,
      setPage,
      openRelabelDialog,
      closeRelabelDialog,
      openRemoveDialog,
      closeRemoveDialog,
    };

    return <AssetsContext value={values}>{children}</AssetsContext>;
  },
  AssetBlockSkeleton,
);

export { AssetsContextProvider, useAssetsContext };
